// The member directory, administered — `vtc/directory/*`
// (docs/03-vtc/member-directory.md).
//
// The schema is the community's: every administrator reads it, and a holder of
// `vtc.directory.admin` defines, changes and retires fields. A change that
// **widens** a field — a higher ceiling or default, searchable, a facet,
// re-enabled, no longer required — takes the requester's passkey gesture and
// is parked for the other holders' approval (VTI-DIR-024), so it is sent
// through `postSignedWithStepUp` and may come back as a `ParkedAction`.
//
// Cards are the members': an administrator reads every entry whole, and
// writes only the values of `admin`-sourced fields (`card/assert`). Nothing
// here changes what a member chose to show.
//
// The disclosure statement (`vtc/directory/disclosure`, VTI-DIR-130) is what
// every applicant is shown before applying, generated by the daemon from the
// configuration above. The console only reads it back, in the words an
// applicant would be given, so an administrator sees the consequence of the
// schema they keep.

import { postSignedRead, postSignedTrustTask } from "./api";
import { postSignedWithStepUp, type ConfirmGesture } from "./signed-act";
import type {
  DirectoryCardAssertPayload,
  DirectoryCardAssertResponse,
  DirectoryDisclosure,
  DirectoryDisclosureResponse,
  DirectoryEntriesListPayload,
  DirectoryEntriesListResponse,
  DirectoryEntriesShowResponse,
  DirectoryImagesGetResponse,
  DirectoryFieldDefinition,
  DirectoryFieldInput,
  DirectoryFieldsListResponse,
  DirectoryFieldsUpdatePayload,
  DirectoryAdminNote,
  DirectoryExportResponse,
  DirectoryNotesExportResponse,
  DirectoryNotesListResponse,
} from "./wire-types";

export const FIELDS_LIST_TASK = "https://trusttasks.org/spec/vtc/directory/fields/list/0.1";
export const FIELDS_DEFINE_TASK = "https://trusttasks.org/spec/vtc/directory/fields/define/0.1";
export const FIELDS_UPDATE_TASK = "https://trusttasks.org/spec/vtc/directory/fields/update/0.1";
export const FIELDS_RETIRE_TASK = "https://trusttasks.org/spec/vtc/directory/fields/retire/0.1";
export const CARD_ASSERT_TASK = "https://trusttasks.org/spec/vtc/directory/card/assert/0.1";
export const ENTRIES_LIST_TASK = "https://trusttasks.org/spec/vtc/directory/entries/list/0.2";
export const ENTRIES_SHOW_TASK = "https://trusttasks.org/spec/vtc/directory/entries/show/0.1";
export const DISCLOSURE_TASK = "https://trusttasks.org/spec/vtc/directory/disclosure/0.1";
export const NOTES_APPEND_TASK = "https://trusttasks.org/spec/vtc/directory/notes/append/0.1";
export const NOTES_LIST_TASK = "https://trusttasks.org/spec/vtc/directory/notes/list/0.2";
export const NOTES_EXPORT_TASK = "https://trusttasks.org/spec/vtc/directory/notes/export/0.1";
export const EXPORT_TASK = "https://trusttasks.org/spec/vtc/directory/export/0.2";
export const IMAGES_GET_TASK = "https://trusttasks.org/spec/vtc/directory/images/get/0.1";

/** `entries/list`'s largest page (`list-limits.json`). */
const ENTRIES_PAGE = 100;
/** How many pages {@link listAllEntries} reads before it stops. */
const MAX_ENTRY_PAGES = 20;
/** Administrators' notes about members: apart from `vtc.directory.admin`. */
export const DIRECTORY_NOTES = "vtc.directory.notes";

/** Who may administer the directory's schema and its asserted values. */
export const DIRECTORY_ADMIN = "vtc.directory.admin";

export type Audience = DirectoryFieldDefinition["ceiling"];
export type FieldType = DirectoryFieldDefinition["type"];

/** The audiences, narrowest first, with what each means. */
export const AUDIENCES: readonly { id: Audience; label: string; hint: string }[] = [
  { id: "admins", label: "Administrators", hint: "holders of vtc.directory.admin" },
  { id: "groups", label: "Group members", hint: "members sharing a community group" },
  { id: "connections", label: "Connections", hint: "members with a verified relationship" },
  { id: "members", label: "Members", hint: "every active member" },
  { id: "public", label: "Public", hint: "anyone, on the member's own published card" },
];

/** Whether every viewer of `inner` is a viewer of `outer`. `groups` and
 *  `connections` do not cover each other — the daemon's `Audience::within`. */
export function within(inner: Audience, outer: Audience): boolean {
  if (inner === outer || inner === "admins") return true;
  if ((inner === "groups" || inner === "connections") && (outer === "members" || outer === "public"))
    return true;
  return inner === "members" && outer === "public";
}

/** Whether a change from `before` to `after` would widen the field, and so be
 *  parked for approval — the daemon's `FieldChange::widens`. Shown before the
 *  change is sent, never relied on: the daemon decides. */
export function widens(before: DirectoryFieldDefinition, after: DirectoryFieldDefinition): boolean {
  return (
    !within(after.ceiling, before.ceiling) ||
    !within(after.defaultVisibility, before.defaultVisibility) ||
    (after.searchable && !before.searchable) ||
    (after.facet && !before.facet) ||
    (after.state === "active" && before.state !== "active") ||
    (!after.required && before.required)
  );
}

export const FIELD_TYPES: readonly FieldType[] = [
  "text",
  "longText",
  "url",
  "email",
  "phone",
  "enum",
  "multiEnum",
  "date",
  "country",
  "did",
];

/** A community field's key: reverse-DNS, as `FieldKey` admits it. */
export const COMMUNITY_KEY_PATTERN = /^([a-z][a-z0-9-]*\.)+[a-z][a-zA-Z0-9]{0,63}$/;

export async function listFields(includeRetired = false): Promise<DirectoryFieldsListResponse> {
  return postSignedRead<DirectoryFieldsListResponse>(FIELDS_LIST_TASK, { includeRetired });
}

export async function defineField(
  field: DirectoryFieldInput,
  reason?: string,
): Promise<DirectoryFieldDefinition> {
  const body = await postSignedTrustTask<{ field: DirectoryFieldDefinition }>(
    FIELDS_DEFINE_TASK,
    { field, ...(reason ? { reason } : {}) },
  );
  return body.field;
}

/** Change a field. A widening is parked: the gesture is asked for, and the
 *  call throws a `ParkedAction` once it is sent for approval. */
export async function updateField(
  change: DirectoryFieldsUpdatePayload,
  confirmGesture: ConfirmGesture,
): Promise<DirectoryFieldDefinition> {
  const body = await postSignedWithStepUp<{ field: DirectoryFieldDefinition }>(
    FIELDS_UPDATE_TASK,
    change,
    confirmGesture,
  );
  return body.field;
}

export async function retireField(key: string, reason: string): Promise<DirectoryFieldDefinition> {
  const body = await postSignedTrustTask<{ field: DirectoryFieldDefinition }>(
    FIELDS_RETIRE_TASK,
    { key, reason },
  );
  return body.field;
}

export async function listEntries(
  query: DirectoryEntriesListPayload,
): Promise<DirectoryEntriesListResponse> {
  return postSignedRead<DirectoryEntriesListResponse>(ENTRIES_LIST_TASK, query);
}

/** Every entry an administrator reads, a page at a time, up to
 *  {@link MAX_ENTRY_PAGES} pages — what the Members list joins on by DID. */
export async function listAllEntries(): Promise<DirectoryEntriesListResponse["items"]> {
  const out: DirectoryEntriesListResponse["items"] = [];
  let cursor: string | undefined;
  for (let i = 0; i < MAX_ENTRY_PAGES; i++) {
    const page = await listEntries({ limit: ENTRIES_PAGE, ...(cursor ? { cursor } : {}) });
    out.push(...page.items);
    if (typeof page.nextCursor !== "string") break;
    cursor = page.nextCursor;
  }
  return out;
}

/** One picture on an entry, read through the administrator's projection. */
export async function getImage(
  entryId: string,
  field: string,
  sha256: string,
): Promise<DirectoryImagesGetResponse> {
  return postSignedRead<DirectoryImagesGetResponse>(IMAGES_GET_TASK, { entryId, field, sha256 });
}

export async function showEntry(entryId: string): Promise<DirectoryEntriesShowResponse> {
  return postSignedRead<DirectoryEntriesShowResponse>(ENTRIES_SHOW_TASK, { entryId });
}

export async function showEntryByDid(did: string): Promise<DirectoryEntriesShowResponse> {
  return postSignedRead<DirectoryEntriesShowResponse>(ENTRIES_SHOW_TASK, { did });
}

export async function assertValues(
  payload: DirectoryCardAssertPayload,
): Promise<DirectoryCardAssertResponse> {
  return postSignedTrustTask<DirectoryCardAssertResponse>(CARD_ASSERT_TASK, payload);
}

// ─── administrators' notes (VTI-DIR-030 – 033) ──────────────────────────────
//
// Kept apart from cards and never in a directory read: only holders of
// `vtc.directory.notes` read or write them. A note is never edited — a
// correction is a later note that redacts it — and releasing a member's notes
// to them takes the operator's gesture bound to that one export.

/** One page of a member's notes, newest first; a redacted note has no text. */
export async function listNotes(entryId: string, cursor?: string): Promise<DirectoryNotesListResponse> {
  return postSignedRead<DirectoryNotesListResponse>(NOTES_LIST_TASK, {
    entryId,
    ...(cursor ? { cursor } : {}),
  });
}

export async function appendNote(
  entryId: string,
  text: string,
  followUp?: string,
): Promise<DirectoryAdminNote> {
  const body = await postSignedTrustTask<{ note: DirectoryAdminNote }>(NOTES_APPEND_TASK, {
    entryId,
    text,
    ...(followUp ? { followUp } : {}),
  });
  return body.note;
}

/** Withdraw an earlier note. Its text stays in the export, and nowhere else. */
export async function redactNote(entryId: string, noteId: string): Promise<DirectoryAdminNote> {
  const body = await postSignedTrustTask<{ note: DirectoryAdminNote }>(NOTES_APPEND_TASK, {
    entryId,
    redacts: noteId,
  });
  return body.note;
}

/** Every note about a member, as written, to answer their request for the data
 *  held about them. Takes the operator's gesture bound to it. */
export async function exportNotes(
  entryId: string,
  reason: string,
  confirmGesture: ConfirmGesture,
): Promise<DirectoryNotesExportResponse> {
  return postSignedWithStepUp<DirectoryNotesExportResponse>(
    NOTES_EXPORT_TASK,
    { entryId, reason },
    confirmGesture,
  );
}

/** Every active member's card as JSContact, read to the end. The first page
 *  takes the operator's gesture; the cursor carries the rest. */
export async function exportDirectory(
  reason: string,
  confirmGesture: ConfirmGesture,
): Promise<Record<string, unknown>[]> {
  const base = { format: "jscontact", reason, limit: 500 };
  const first = await postSignedWithStepUp<DirectoryExportResponse>(EXPORT_TASK, base, confirmGesture);
  const cards = [...(first.items as Record<string, unknown>[])];
  let cursor = first.nextCursor;
  while (typeof cursor === "string") {
    const page = await postSignedTrustTask<DirectoryExportResponse>(EXPORT_TASK, { ...base, cursor });
    cards.push(...(page.items as Record<string, unknown>[]));
    cursor = page.nextCursor;
  }
  return cards;
}

/** Offer `value` as a JSON file the browser saves. */
export function saveJson(value: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** The statement every applicant is shown, as the daemon generates it now. */
export async function readDisclosure(): Promise<DirectoryDisclosureResponse> {
  return postSignedRead<DirectoryDisclosureResponse>(DISCLOSURE_TASK, {});
}

/** Who an audience is, in a sentence: "other members", "anyone". */
const AUDIENCE_PHRASE: Record<Audience, string> = {
  admins: "administrators only",
  groups: "members who share a group with them",
  connections: "members they have a verified relationship with",
  members: "other members",
  public: "anyone, outside the community too",
};

/** An ISO 8601 duration in words where it is whole days or weeks, else as given. */
export function durationWords(iso: string): string {
  const m = /^P(?:(\d+)W)?(?:(\d+)D)?$/.exec(iso);
  if (!m || (!m[1] && !m[2])) return iso;
  const days = Number(m[1] ?? 0) * 7 + Number(m[2] ?? 0);
  return days === 1 ? "1 day" : `${days} days`;
}

function labels(fields: DirectoryDisclosure["fields"]): string {
  const names = fields.map((f) => f.label);
  return names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/**
 * The statement as an applicant would be told it: one sentence per thing it
 * states, in plain words. Field labels and descriptions are the community's
 * own text and stay text.
 */
export function describeDisclosure(d: DirectoryDisclosure): string[] {
  const out: string[] = [];
  const shownByDefault = AUDIENCES.filter((a) => a.id !== "admins")
    .map((a) => ({ a: a.id, fields: d.fields.filter((f) => f.defaultVisibility === a.id) }))
    .filter((g) => g.fields.length > 0);
  for (const g of shownByDefault) {
    out.push(`A new member's ${labels(g.fields)} ${g.fields.length === 1 ? "is" : "are"} seen by ${AUDIENCE_PHRASE[g.a]} until they change it.`);
  }
  out.push(
    shownByDefault.length > 0
      ? "Everything else on a new card is seen by administrators only until its member chooses otherwise."
      : "Everything on a new card is seen by administrators only until its member chooses otherwise.",
  );
  const asserted = d.fields.filter((f) => f.source === "admin");
  if (asserted.length > 0) {
    out.push(`The community writes ${labels(asserted)} itself; members read ${asserted.length === 1 ? "it" : "them"} and cannot change ${asserted.length === 1 ? "it" : "them"}.`);
  }
  const listing = {
    listed: "appears when members browse the directory",
    findable: "does not appear when members browse, but a search can find it",
    hidden: "is not shown to members browsing or searching",
  }[d.newCard.listing];
  out.push(`A new entry ${listing}; every member can hide theirs.`);
  out.push(
    d.membershipIdentifiers === "admins"
      ? "The DID a member joins with is seen by administrators only."
      : `The DID a member joins with is seen by administrators only, unless the member chooses to show it to ${AUDIENCE_PHRASE[d.membershipIdentifiers]}.`,
  );
  out.push(
    d.adminNotes
      ? "Administrators may keep notes about members. Notes never appear on a card."
      : "Administrators keep no notes about members.",
  );
  if (d.groups) {
    out.push(`Members of a community group hold a credential showing it, valid for ${durationWords(d.groups.credentialValidity)} at a time, which they can present outside the community.`);
  }
  out.push(
    d.externalCards
      ? "A member may publish their card beyond the community; it is off until they turn it on."
      : "No card is ever published outside the community.",
  );
  return out;
}

/** The widest audience each field can reach, for the table beside the words. */
export function audiencePhrase(a: Audience): string {
  return AUDIENCE_PHRASE[a];
}
